/**
 * The app's tab shell on the phone website (shared-client-screens D3, rows S1 to S8, N1 to N6): a
 * pathless layout route around Home, Invite, Waitlists, Bookings and Profile. On a phone it draws
 * the app's own ClientShellFrame, which stays mounted across those pages as the app's does, fed the
 * website session's dashboard, and gives its pages the app's ClientShellContext (the dashboard,
 * Book, the header slot, the scroll reset), so a tab page reads `useClientShellContext()` on both
 * sides. On a computer, for the guest home and for an account page signed out (its sign-in step),
 * it is a plain Outlet. Invite and Waitlists are display only: a visitor gets them in the frame
 * too, without a dashboard.
 *
 * The bar is the app's BottomNavView with the website's tabs (`PhoneBar`): signed in, Home, Bookings,
 * Invite and Profile; for a visitor Profile reads Sign in, and Bookings and Invite start the
 * sign-in, keeping them on the page. Book opens the catalog tab the studio has something on
 * (resolveBookTab, Decision C), says "No services or events available" when nothing is bookable,
 * and waits while the studio read is pending.
 */
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom';

import { BottomNavView, type BottomNavTab, type BottomNavTabKey } from '@/components/client/ClientShell/BottomNavView';
import type { ClientShellContext } from '@/components/client/ClientShell/ClientShellContext';
import { ClientShellFrame, type ClientShellStatus } from '@/components/client/ClientShell/ClientShellFrame';
import { useToast } from '@/components/Toast/ToastContext';
import { useRereadOnPathChange } from '@/hooks/useRereadOnPathChange';
import { resolveBookTab } from '@/utils/bookTarget';

import { useWebSession } from '../api/session';
import { useSiteStudio } from '../api/site';
import { accountKeys, useWebDashboard } from '../hooks/useWebAccount';
import { useAppScreens } from '../ui/useIsPhone';
import { usePhoneSignIn } from './PhoneDetailFrame';

const TAB_PATHS: Record<BottomNavTabKey, string> = {
  home: '/',
  bookings: '/account/bookings',
  invite: '/invite',
  profile: '/account/profile',
};

/** The pages a visitor also gets in the frame: display only. */
const VISITOR_PAGES = new Set(['/invite', '/waitlists']);

/** What the frame gives a visitor's page: no dashboard, the rest as the app's. */
export type PhoneTabsVisitorContext = Omit<ClientShellContext, 'dashboard'> & { dashboard: null };

/** The frame's context, for a page a visitor also opens (Invite, Waitlists); signed-in pages read `useClientShellContext()`. */
export function usePhoneTabsContext(): ClientShellContext | PhoneTabsVisitorContext {
  return useOutletContext<ClientShellContext | PhoneTabsVisitorContext>();
}

/** Book on the website: the tab the studio has something on, or a toast when nothing is bookable. */
function usePhoneBook(): { onBook: () => void; bookDisabled: boolean } {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const studio = useSiteStudio().data;
  const onBook = useCallback(() => {
    if (!studio) return;
    // The app opens its one catalog on that tab; the website has each tab at its own address.
    const tab = resolveBookTab({ hasServices: studio.hasServices, hasEvents: studio.hasEvents });
    if (tab) navigate(`/${tab}`);
    else showToast('No services or events available', 'info');
  }, [navigate, showToast, studio]);
  return { onBook, bookDisabled: !studio };
}

/** The app's bottom bar with the website's tabs and Book; `current` is the tab of the page on show. */
export function PhoneBar({ current }: { current?: BottomNavTabKey }) {
  const navigate = useNavigate();
  const signIn = usePhoneSignIn();
  const signedIn = useWebSession().status === 'signedIn';
  const { onBook, bookDisabled } = usePhoneBook();
  const tabs = useMemo<BottomNavTab[]>(() => {
    const go = (key: BottomNavTabKey) => (key === current ? () => undefined : () => navigate(TAB_PATHS[key]));
    return [
      { key: 'home', label: 'Home', current: current === 'home', onPress: go('home') },
      { key: 'bookings', label: 'Bookings', current: current === 'bookings', onPress: signedIn ? go('bookings') : signIn },
      { key: 'invite', label: 'Invite', current: current === 'invite', onPress: signedIn ? go('invite') : signIn },
      signedIn
        ? { key: 'profile', label: 'Profile', current: current === 'profile', onPress: go('profile') }
        : { key: 'profile', label: 'Sign in', current: false, onPress: signIn },
    ];
  }, [current, navigate, signIn, signedIn]);
  return <BottomNavView tabs={tabs} onBook={onBook} bookDisabled={bookDisabled} />;
}

function tabOf(pathname: string): BottomNavTabKey | undefined {
  return (Object.keys(TAB_PATHS) as BottomNavTabKey[]).find((key) => TAB_PATHS[key] === pathname);
}

function PhoneTabsFrame({ signedIn }: { signedIn: boolean }) {
  const { pathname } = useLocation();
  const contentRef = useRef<HTMLElement>(null);
  const dashboard = useWebDashboard();
  const { onBook } = usePhoneBook();
  // The frame stays mounted across its pages: re-read the dashboard on each, as the app's shell does.
  useRereadOnPathChange(accountKeys.dashboard);

  const [headerSlot, setHeaderSlot] = useState<ReactNode>(null);
  const scrollContentToTop = useCallback(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, []);

  const data = signedIn ? dashboard.data : undefined;
  const context = useMemo<ClientShellContext | PhoneTabsVisitorContext | null>(() => {
    if (!signedIn) return { dashboard: null, onBook, setHeaderSlot, scrollContentToTop };
    return data ? { dashboard: data, onBook, setHeaderSlot, scrollContentToTop } : null;
  }, [data, onBook, scrollContentToTop, signedIn]);

  const status: ClientShellStatus = !signedIn
    ? 'ready'
    : dashboard.error
      ? // The website's fixed line, as before: a failed read's own message is never shown to a client.
        { title: 'Unable to load', message: 'Please try again later' }
      : data
        ? 'ready'
        : 'loading';

  return (
    <ClientShellFrame
      status={status}
      dashboard={data}
      isHome={pathname === '/'}
      headerSlot={headerSlot}
      contentRef={contentRef}
      nav={<PhoneBar current={tabOf(pathname)} />}
      scrollKey={pathname}
    >
      {context && <Outlet context={context} />}
    </ClientShellFrame>
  );
}

export function PhoneTabsLayout() {
  const phone = useAppScreens();
  const session = useWebSession().status;
  const { pathname } = useLocation();
  const signedIn = session === 'signedIn';
  if (!phone || (!signedIn && !VISITOR_PAGES.has(pathname))) return <Outlet />;
  return <PhoneTabsFrame signedIn={signedIn} />;
}
